import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { getProjects } from '../utils/api';

// ─── TOAST CONTEXT ─────────────────────────────────────────────────────────
const ToastContext = createContext(null);
export const useToast = () => useContext(ToastContext);

// ─── AI BADGE ──────────────────────────────────────────────────────────────
export const AIBadge = ({ label = 'AI' }) => (
  <span className="ai-badge">✦ {label}</span>
);

// ─── LIFECYCLE UTILS (shared across app) ───────────────────────────────────
export const LIFECYCLE_STATES = [
  { key: 'draft_scope',              label: 'Draft Scope',          group: 'scope'    },
  { key: 'scope_review',             label: 'Scope Review',         group: 'scope'    },
  { key: 'ready_for_rfq',            label: 'Ready for RFQ',        group: 'rfq'      },
  { key: 'rfq_drafted',              label: 'RFQ Drafted',          group: 'rfq'      },
  { key: 'rfq_approved',             label: 'RFQ Approved',         group: 'rfq'      },
  { key: 'rfq_sent',                 label: 'RFQ Sent',             group: 'rfq'      },
  { key: 'awaiting_quotes',          label: 'Awaiting Quotes',      group: 'quotes'   },
  { key: 'quotes_received',          label: 'Quotes Received',      group: 'quotes'   },
  { key: 'bid_leveling',             label: 'Bid Leveling',         group: 'award'    },
  { key: 'award_recommendation',     label: 'Award Recommendation', group: 'award'    },
  { key: 'award_approved',           label: 'Award Approved',       group: 'award'    },
  { key: 'contract_po_pending',      label: 'Contract/PO Pending',  group: 'post'     },
  { key: 'contract_po_issued',       label: 'Contract/PO Issued',   group: 'post'     },
  { key: 'submittals_pending',       label: 'Submittals Pending',   group: 'post'     },
  { key: 'submittals_approved',      label: 'Submittals Approved',  group: 'post'     },
  { key: 'delivery_scheduled',       label: 'Delivery Scheduled',   group: 'post'     },
  { key: 'delivered',                label: 'Delivered',            group: 'post'     },
  { key: 'invoice_pending',          label: 'Invoice Pending',      group: 'post'     },
  { key: 'invoice_matched',          label: 'Invoice Matched',      group: 'post'     },
  { key: 'closed',                   label: 'Closed',               group: 'closed'   },
];

export const getLifecycleLabel = (key) =>
  LIFECYCLE_STATES.find(s => s.key === key)?.label || key;

export const getLifecycleCssClass = (key) => {
  const group = LIFECYCLE_STATES.find(s => s.key === key)?.group;
  const map = { scope: 'draft', rfq: 'review', quotes: 'sent', award: 'active', post: 'awarded', closed: 'closed' };
  return `lifecycle-${map[group] || 'draft'}`;
};

// ─── NAV CONFIG ────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/control',    label: 'Project Control', icon: '⬡' },
  { path: '/documents',  label: 'Documents',       icon: '◫' },
  { path: '/extract',    label: 'Extracted Items', icon: '◈' },
  { path: '/packages',   label: 'Packages',        icon: '▦' },
  { path: '/rfq',        label: 'RFQ & Responses', icon: '◎' },
  { path: '/bid-leveling', label: 'Bid Leveling',  icon: '◑' },
  { path: '/suppliers',  label: 'Suppliers',       icon: '◉' },
  { path: '/work-queue', label: 'AI Work Queue',   icon: '✦' },
];

const MOBILE_NAV = [
  { path: '/control',   label: 'Control',   icon: '⬡' },
  { path: '/packages',  label: 'Packages',  icon: '▦' },
  { path: '/rfq',       label: 'RFQ',       icon: '◎' },
  { path: '/suppliers', label: 'Suppliers', icon: '◉' },
  { path: '/work-queue',label: 'Queue',     icon: '✦' },
];

// ─── LAYOUT ────────────────────────────────────────────────────────────────
export default function Layout() {
  const [collapsed, setCollapsed]       = useState(false);
  const [projects, setProjects]         = useState([]);
  const [selectedProject, setSelected] = useState(null);
  const [toasts, setToasts]             = useState([]);
  const [cmdOpen, setCmdOpen]           = useState(false);
  const [cmdQuery, setCmdQuery]         = useState('');
  const [notifications, setNotifs]      = useState([]);
  const [notifsOpen, setNotifsOpen]     = useState(false);
  const [workQueueCount, setWQCount]    = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Toast system
  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  // ── Load projects
  useEffect(() => {
    getProjects().then(data => {
      setProjects(data || []);
      const saved = localStorage.getItem('selectedProject');
      const found = data?.find(p => p.id === parseInt(saved));
      setSelected(found || data?.[0] || null);
    }).catch(() => {});
  }, []);

  // ── CMD+K listener
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen(o => !o); }
      if (e.key === 'Escape') { setCmdOpen(false); setNotifsOpen(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const switchProject = (project) => {
    setSelected(project);
    localStorage.setItem('selectedProject', project.id);
    window.location.href = window.location.pathname;
  };

  const cmdResults = NAV_ITEMS.filter(n =>
    n.label.toLowerCase().includes(cmdQuery.toLowerCase())
  );

  return (
    <ToastContext.Provider value={addToast}>
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--color-background)' }}>

        {/* ── SIDEBAR ─────────────────────────────────────────────────────── */}
        <aside style={{
          width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
          background: 'var(--color-navy)',
          display: 'flex', flexDirection: 'column',
          transition: 'width 0.2s ease', flexShrink: 0,
          position: 'relative', zIndex: 100,
        }}>
          {/* Logo */}
          <div style={{
            padding: collapsed ? '20px 0' : '20px 20px',
            display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}>
            {!collapsed && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.02em' }}>Procura</span>
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6875rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>AI Procurement</span>
              </div>
            )}
            {collapsed && <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.125rem' }}>P</span>}
            <button
              onClick={() => setCollapsed(c => !c)}
              style={{
                background: 'rgba(255,255,255,0.06)', border: 'none',
                color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
                borderRadius: 'var(--radius-md)', padding: '6px', fontSize: '0.875rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {collapsed ? '→' : '←'}
            </button>
          </div>

          {/* Project selector */}
          {!collapsed && (
            <div style={{ padding: '12px 12px 0' }}>
              <div style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 'var(--radius-md)', padding: '8px 12px',
              }}>
                <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Project</div>
                <select
                  value={selectedProject?.id || ''}
                  onChange={e => {
                    const p = projects.find(p => p.id === parseInt(e.target.value));
                    if (p) switchProject(p);
                  }}
                  style={{
                    background: 'transparent', border: 'none', color: '#fff',
                    fontFamily: 'var(--font-main)', fontSize: '0.875rem', fontWeight: 500,
                    width: '100%', cursor: 'pointer', outline: 'none',
                  }}
                >
                  {projects.map(p => (
                    <option key={p.id} value={p.id} style={{ background: 'var(--color-navy)' }}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Nav items */}
          <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto', marginTop: 8 }}>
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center',
                  gap: 10, padding: collapsed ? '10px 0' : '9px 16px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  margin: '1px 8px', borderRadius: 'var(--radius-md)',
                  textDecoration: 'none', transition: 'all 0.15s ease',
                  background: isActive ? 'rgba(13,148,136,0.2)' : 'transparent',
                  color: isActive ? 'var(--color-teal-light)' : 'rgba(255,255,255,0.55)',
                  fontSize: '0.875rem', fontWeight: isActive ? 600 : 400,
                  borderLeft: isActive ? '2px solid var(--color-teal)' : '2px solid transparent',
                })}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && (
                  <span style={{ flex: 1 }}>
                    {item.label}
                    {item.path === '/work-queue' && workQueueCount > 0 && (
                      <span style={{
                        marginLeft: 6, background: 'var(--color-teal)', color: '#fff',
                        fontSize: '0.6875rem', fontWeight: 700, padding: '1px 6px',
                        borderRadius: 'var(--radius-full)',
                      }}>{workQueueCount}</span>
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Sidebar footer */}
          {!collapsed && (
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)',
            }}>
              <div>AI Procurement Department</div>
              <div style={{ marginTop: 2, color: 'rgba(255,255,255,0.2)' }}>v2.0</div>
            </div>
          )}
        </aside>

        {/* ── MAIN AREA ───────────────────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Topbar */}
          <header style={{
            height: 'var(--topbar-height)',
            background: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 24px', flexShrink: 0,
          }}>
            {/* Left: breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-mid)' }}>
                {selectedProject?.name || 'No project selected'}
              </span>
              {selectedProject && (
                <>
                  <span style={{ color: 'var(--color-border-mid)' }}>›</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-text-dark)' }}>
                    {NAV_ITEMS.find(n => location.pathname.startsWith(n.path))?.label || 'Project Control'}
                  </span>
                </>
              )}
            </div>

            {/* Right: actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* CMD+K */}
              <button
                onClick={() => setCmdOpen(true)}
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}
              >
                ⌘K Search
              </button>

              {/* Notifications */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setNotifsOpen(o => !o)}
                  className="btn-icon btn-ghost"
                  style={{ position: 'relative', fontSize: '1rem' }}
                >
                  🔔
                  {notifications.length > 0 && (
                    <span style={{
                      position: 'absolute', top: 2, right: 2,
                      width: 8, height: 8, background: 'var(--color-error)',
                      borderRadius: '50%',
                    }} />
                  )}
                </button>
                {notifsOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-lg)', width: 320,
                    boxShadow: 'var(--shadow-dropdown)', zIndex: 200,
                  }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', fontWeight: 600, fontSize: '0.875rem' }}>
                      Notifications
                    </div>
                    <div style={{ padding: 16 }}>
                      {notifications.length === 0 ? (
                        <p style={{ textAlign: 'center', color: 'var(--color-text-light)' }}>All clear</p>
                      ) : (
                        notifications.map((n, i) => (
                          <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem' }}>{n.message}</div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User */}
              <button
                onClick={() => { localStorage.removeItem('token'); navigate('/login'); }}
                className="btn btn-ghost btn-sm"
                style={{ fontSize: '0.75rem' }}
              >
                Sign out
              </button>
            </div>
          </header>

          {/* Page content */}
          <main style={{ flex: 1, overflowY: 'auto' }}>
            <Outlet context={{ selectedProject, projects, addToast, setWQCount }} />
          </main>
        </div>

        {/* ── MOBILE NAV ──────────────────────────────────────────────────── */}
        <nav style={{
          display: 'none',
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'var(--color-navy)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          zIndex: 500, padding: '8px 0 env(safe-area-inset-bottom)',
        }} className="mobile-nav">
          {MOBILE_NAV.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              style={({ isActive }) => ({
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                color: isActive ? 'var(--color-teal-light)' : 'rgba(255,255,255,0.4)',
                textDecoration: 'none', fontSize: '0.625rem', padding: '4px 8px',
              })}
            >
              <span style={{ fontSize: '1.125rem' }}>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* ── CMD PALETTE ─────────────────────────────────────────────────── */}
        {cmdOpen && (
          <>
            <div className="modal-overlay" onClick={() => setCmdOpen(false)} />
            <div style={{
              position: 'fixed', top: '30%', left: '50%', transform: 'translateX(-50%)',
              width: 480, background: 'var(--color-surface)',
              borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-modal)',
              zIndex: 1100, overflow: 'hidden',
              animation: 'slideUp 0.15s ease',
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: 'var(--color-text-light)' }}>⌘</span>
                <input
                  autoFocus
                  value={cmdQuery}
                  onChange={e => setCmdQuery(e.target.value)}
                  placeholder="Search pages, packages, suppliers..."
                  style={{ flex: 1, border: 'none', outline: 'none', fontFamily: 'var(--font-main)', fontSize: '0.9375rem', color: 'var(--color-text-dark)' }}
                />
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {cmdResults.map(item => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setCmdOpen(false); setCmdQuery(''); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 16px', background: 'none', border: 'none',
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'background 0.1s',
                      fontFamily: 'var(--font-main)', fontSize: '0.875rem', color: 'var(--color-text-dark)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <span style={{ fontSize: '1rem', width: 24, textAlign: 'center' }}>{item.icon}</span>
                    {item.label}
                  </button>
                ))}
                {cmdResults.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-light)', fontSize: '0.875rem' }}>
                    No results for "{cmdQuery}"
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── TOASTS ──────────────────────────────────────────────────────── */}
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast-${t.type}`}>
              <span style={{ flex: 1 }}>{t.message}</span>
              <button
                onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1rem' }}
              >×</button>
            </div>
          ))}
        </div>

      </div>
    </ToastContext.Provider>
  );
}